import React from 'react';
import { Conversation } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { UserRole } from '@uplift/types';
import { usePatientNextAppointmentsQuery, useProviderNextAppointmentPerPatientQuery } from '@uplift/queries';
import { formatDateTimeRange } from '@uplift/services';
import { LoadableTypography } from '../../LoadableTypography';
import { Chip } from '../../Chip';
import { useL10n } from '@uplift/hooks';
import { Color } from '../types';


const ProviderSubtitle: React.FC<{ conversation: Conversation }> = ({ conversation }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const { data: nextAppointment, isLoading: isNextAppointmentLoading } = useProviderNextAppointmentPerPatientQuery(conversation.participantId, {
    enabled: me.role === UserRole.provider,
  });

  const nextSessionLabel = React.useMemo(() => {
    if (!nextAppointment?.fromDatetime) return 'No upcoming appointment';
    return formatDateTimeRange(nextAppointment.fromDatetime, nextAppointment.toDatetime, timezone);
  }, [ nextAppointment, timezone ]);

  return (
    <LoadableTypography component="span" minWidth="180px" variant="body2" loading={isNextAppointmentLoading} title="Upcoming appointment">
      {nextSessionLabel}
    </LoadableTypography>
  );
};

const PatientSubtitle: React.FC<{ conversation: Conversation }> = ({ conversation }) => {
  const timezone = useTimezone();
  const { data: nextAppointments, isLoading: isNextAppointmentLoading } = usePatientNextAppointmentsQuery({
    appointments_per_series: 1,
    limit: 10,
  });
  const nextAppointment = nextAppointments?.find(a => a.providerId === conversation.participantId);

  const nextSessionLabel = React.useMemo(() => {
    if (!nextAppointment?.fromDatetime) return 'No upcoming appointment';
    return formatDateTimeRange(nextAppointment.fromDatetime, nextAppointment.toDatetime, timezone);
  }, [ nextAppointment, timezone ]);

  return (
    <LoadableTypography component="span" minWidth="180px" variant="body2" loading={isNextAppointmentLoading} title="Upcoming appointment">
      {nextSessionLabel}
    </LoadableTypography>
  );
};


export const ConversationItemSubtitle: React.FC<{ color: Color; conversation: Conversation }> = ({ color, conversation }) => {
  const l10n = useL10n();
  const me = useCurrentuser();

  if (me.role === UserRole.provider && conversation.participantRole === UserRole.patient) {
    return <ProviderSubtitle conversation={conversation} />;
  }
  if (me.role === UserRole.patient && conversation.participantRole === UserRole.provider) {
    return <PatientSubtitle conversation={conversation} />;
  }
  if (me.role === UserRole.superadmin) {
    return <Chip component="span" variant="outlined" size="small" label={l10n.resources.user.role[conversation.participantRole]} color={color} />;
  }
  if (conversation.participantRole === UserRole.superadmin) {
    return <Chip component="span" variant="outlined" size="small" label="Admin" color={color} />;
  }

  return null;
};
